import type { Theme } from '@mui/material/styles';
import { styled, useTheme } from '@mui/material/styles';
import type { GroupBase, StylesConfig } from 'react-select';

const getOptionBackgroundColor = (
  theme: Theme,
  isSelected: boolean,
  isFocused: boolean
): string => {
  if (isSelected) {
    return isFocused ? theme.palette.primary.light : theme.palette.primary.lighter;
  }
  return isFocused ? theme.palette.action.hover : 'transparent';
};

const baseControlBorderColor = (
  theme: Theme,
  error: boolean,
  isFocused: boolean,
  defaultColor: string
) => {
  if (error) return theme.palette.error.main;
  if (isFocused) return theme.palette.primary.main;

  return defaultColor;
};

export const useCustomStyles = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  error: boolean
): StylesConfig<Option, IsMulti, Group> => {
  const theme = useTheme();

  return {
    control: (base, state) => ({
      ...base,
      width: '100%',
      minHeight: '37px',
      borderRadius: theme.shape.borderRadius,
      borderColor: baseControlBorderColor(theme, error, state.isFocused, theme.palette.grey[300]),
      borderWidth: state.isFocused ? '2px' : '1px',
      boxShadow: 'none',
      '&:hover': {
        borderColor: baseControlBorderColor(theme, error, state.isFocused, theme.palette.grey[800]),
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: `0px ${theme.spacing(0.5)}`,
      paddingRight: `${theme.spacing(4)}`,
      overflow: 'hidden',
    }),
    input: (base) => ({
      ...base,
      color: theme.palette.text.primary,
    }),
    placeholder: (base, state) => ({
      ...base,
      color: theme.palette.grey[500],
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '&::after': state.selectProps.required
        ? {
            content: '" *"',
            color: theme.palette.error.main,
          }
        : {},
    }),
    menu: (base) => ({
      ...base,
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[8],
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: theme.zIndex.modal + 100,
    }),
    option: (base, state) => ({
      ...base,
      width: 'auto',
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1),
      margin: theme.spacing(1),
      backgroundColor: getOptionBackgroundColor(theme, state.isSelected, state.isFocused),
      color: state.isDisabled ? theme.palette.text.disabled : theme.palette.text.primary,
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      opacity: state.isDisabled ? 0.6 : 1,
      borderRadius: theme.shape.borderRadius,
      '&:active': {
        backgroundColor: state.isDisabled
          ? undefined
          : state.isSelected
            ? theme.palette.primary.light
            : theme.palette.action.hover,
      },
    }),
    container: (base) => ({
      ...base,
      width: '100%',
    }),
    clearIndicator: (base) => ({
      ...base,
      position: 'absolute',
      right: theme.spacing(1),
      padding: 0,
    }),
  };
};

export const SelectedValuesContainer = styled('div')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(0.5),
  padding: theme.spacing(1),
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
}));
